// Part 11c — the real patient-facing monthly digest page: the page the
// "your digest is ready" email actually links to (see backend main.py's
// _send_monthly_digest_ready_email / DIGEST_PAGE_URL). Reads GET
// /my-monthly-digest (this same app's backend) — mirrors MySummaries.js's
// own visual language deliberately (card-based layout, dark-mode support,
// identity header, back-to-home link, ThemeToggle/NavUserMenu) rather than
// inventing a new one, per this piece's own build instruction.
//
// Every decrypt (narrative, news item heading/body_html) already happened
// server-side — this component never holds or needs the PHI encryption
// key, only ever renders what the route already decided was safe to send.
//
// Mandatory correctness rules this component must respect (see the plan
// doc's Part 11c entry and 11b-1/11b-2's own close-out findings):
// (1) each month is rendered from the backend's own per-month
//     identity_header (built from that month's own specimen_id, never a
//     parent-level pointer) — this component does not try to compute or
//     assume that itself;
// (2) report_type_label comes straight from the backend
//     (_digest_report_type_display_label) — never render report_type_key
//     raw;
// (3) news_items can have heading=null (live-search fallback — no
//     per-item body, every item in that case shares ONE drafted body_html
//     blob) or heading=<string> (curated-pool path — one body per item);
//     handled by the two render branches below, never assumed to be one
//     shape;
// (4) an empty/missing news_items list ALWAYS renders the same friendly
//     "no news this month" state — the backend intentionally never sends
//     a skip/failure reason to this component at all, so there is no way
//     for this component to (mis)render a benign default state as an
//     error even by accident;
// (5) every news_items.url is re-validated here too (defense in depth —
//     the backend already checked at write time AND at this read's own
//     serve time) before ever being bound into an <a href>, never trusted
//     blindly a third time.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
// advisor finding, pre-handback — CalendarMonth/plain "Lock" could not be
// confirmed against a real installed node_modules in this environment (no
// node_modules present at all to check against); swapped for History/
// LockOutlined, both already imported successfully elsewhere in this same
// component directory (AdminDashboard.js/AdminClinicians.js's `History as
// HistoryIcon`, PatientConsent.js's `LockOutlined`) against this exact
// installed @mui/icons-material version, so these are confirmed-safe
// rather than merely assumed-safe.
import { ArrowBack, MailOutline, History, LockOutlined } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import UserContext from "./UserContext";
import { useContext } from "react";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import { formatEST } from "../dateUtils";
import { extractErrorDetail } from "./validators";
// 2026-10-18 fix — same convention/precedent as LabReports.js's own
// REPORTS_ENABLED: Home.js only hides the nav card behind
// REACT_APP_MONTHLY_DIGEST_ENABLED, so reaching /my-digest via direct URL
// while the flag is off would still fire this GET unconditionally. Skip it
// when disabled, same friendly-message pattern LabReports.js already uses.
const DIGEST_ENABLED = process.env.REACT_APP_MONTHLY_DIGEST_ENABLED === "true";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

// Defense-in-depth URL check (mandatory correctness rule 5 above) — only
// ever render as a clickable link when the scheme is plain http(s), same
// test the backend's own _digest_url_is_safe_scheme applies, independently
// re-checked here rather than trusted a third time.
const _SAFE_URL_RE = /^https?:\/\//i;
function _safeHref(url) {
  return typeof url === "string" && _SAFE_URL_RE.test(url) ? url : null;
}

// ui-ux-checker finding, pre-integration, real bug — year_month is a bare
// "YYYY-MM" string (e.g. "2026-10"); the backend's own narrative/news
// generation keys are month-granularity only, no day component. Rendering
// it raw showed the literal internal key as the bold heading of every
// month card. Deliberately NOT `new Date(year_month)` — the ECMA-262 Date
// Time String Format parses a bare "YYYY-MM" as UTC midnight, which this
// app's own fixed America/New_York display convention (dateUtils.js's own
// banner comment) would then show as the PREVIOUS month for part of every
// day. Builds a local-time date (day component is never used/shown) so no
// UTC/local offset can shift the displayed month at all.
function _formatYearMonth(ym) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || ""));
  if (!m) return String(ym || "");
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  if (isNaN(d.getTime())) return String(ym);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

function IdentityHeader({ h, isDark }) {
  if (!h) return null;
  const name = [h.patient_first_name, h.patient_last_name].filter(Boolean).join(" ");
  return (
    <Box sx={{
      display: "flex", flexWrap: "wrap", gap: 2, mb: 1.5, px: 1.5, py: 1,
      borderRadius: 1.5, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.04)",
      fontSize: "0.74rem", color: isDark ? "#94a3b8" : "#64748b",
    }}>
      {name && <span><b style={{ color: isDark ? "#cbd5e1" : "#334155" }}>{name}</b></span>}
      {h.patient_dob && <span>DOB: {h.patient_dob}</span>}
      {/* ui-ux-checker finding, pre-integration — this used to say "Report
          date:" for the identical field MySummaries.js's own IdentityHeader
          labels "Uploaded:" (MySummaries.js:454); aligned to the same label
          so the two sibling pages never describe the same field two
          different ways. */}
      {h.generated_at && <span>Uploaded: {formatEST(h.generated_at, { dateOnly: true })}</span>}
      {/* 2026-10-24 — aligned to MySummaries.js's own identical relabel
          (same sibling-consistency discipline as the comment above) and
          its new "Ordered by" addition, now that the backend header
          carries provider_name/practice_name too. */}
      {h.generated_by && <span>Uploaded by: {h.generated_by}</span>}
      {h.provider_name && (
        <span>Ordered by: {h.provider_name}{h.practice_name ? ` — ${h.practice_name}` : ""}</span>
      )}
    </Box>
  );
}

// One curated-pool-path item — its own heading + its own body.
function PoolNewsItem({ item, isDark }) {
  const href = _safeHref(item.url);
  return (
    <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,53,122,0.06)" }}>
      <Typography sx={{ fontSize: "0.86rem", fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b", mb: 0.5 }}>
        {item.heading}
      </Typography>
      <Box sx={{ fontSize: "0.82rem", color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.7, "& p": { mb: 1 }, "& ul": { pl: 3 } }}
        dangerouslySetInnerHTML={{ __html: item.body_html }} />
      {(item.source || href) && (
        <Typography sx={{ fontSize: "0.72rem", color: isDark ? "#64748b" : "#94a3b8", mt: 0.5 }}>
          {item.source || "Source"}
          {href && (
            <> — <a href={href} target="_blank" rel="noopener noreferrer"
              style={{ color: isDark ? BRAND.gold : BRAND.navy }}>
              {item.title || "Read more"}
            </a></>
          )}
          {item.pub_date && <> · {formatEST(item.pub_date, { dateOnly: true })}</>}
        </Typography>
      )}
    </Box>
  );
}

// The live-search-fallback path — every item in this one month shares the
// SAME drafted body_html blob (the backend only ever stores one; see this
// file's own banner comment, point 3), so the body is rendered ONCE here,
// followed by a plain source list, never once per item (which would show
// the identical paragraph repeated up to 4 times).
function LiveSearchNewsGroup({ items, isDark }) {
  if (!items.length) return null;
  const body = items.find((i) => i.body_html)?.body_html;
  return (
    <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,53,122,0.06)" }}>
      {body && (
        <Box sx={{ fontSize: "0.82rem", color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.7, mb: 1, "& p": { mb: 1 }, "& ul": { pl: 3 } }}
          dangerouslySetInnerHTML={{ __html: body }} />
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {items.map((item, i) => {
          const href = _safeHref(item.url);
          return (
            <Typography key={i} sx={{ fontSize: "0.72rem", color: isDark ? "#64748b" : "#94a3b8" }}>
              {href ? (
                <a href={href} target="_blank" rel="noopener noreferrer"
                  style={{ color: isDark ? BRAND.gold : BRAND.navy }}>
                  {item.title || item.source || "Source"}
                </a>
              ) : (item.title || item.source || "Source")}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );
}

function MonthNewsSection({ newsItems, isDark }) {
  const items = Array.isArray(newsItems) ? newsItems.filter((i) => i && i.body_html) : [];
  // Mandatory correctness rule (4) — an empty list ALWAYS renders this
  // same ordinary, friendly state. There is no branch anywhere in this
  // component that can turn a benign default (including the common
  // live-search-disabled default, which the backend deliberately never
  // even tells this component about) into anything that reads as an
  // error.
  if (!items.length) {
    return (
      <Typography sx={{ fontSize: "0.8rem", fontStyle: "italic", color: isDark ? "#64748b" : "#94a3b8" }}>
        No new health news or research for this result this month — check back next month.
      </Typography>
    );
  }
  const poolItems = items.filter((i) => i.heading);
  const liveItems = items.filter((i) => !i.heading);
  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 1, textTransform: "uppercase", letterSpacing: 0.4 }}>
        What's New
      </Typography>
      {poolItems.map((item, i) => <PoolNewsItem key={i} item={item} isDark={isDark} />)}
      <LiveSearchNewsGroup items={liveItems} isDark={isDark} />
    </Box>
  );
}

function MonthCard({ month, isDark }) {
  return (
    <Box sx={{ mb: 2.5, pb: 2.5, "&:last-of-type": { mb: 0, pb: 0, borderBottom: "none" }, borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <History sx={{ fontSize: 16, color: isDark ? "#94a3b8" : "#64748b" }} />
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: isDark ? "#f1f5f9" : BRAND.navy }}>
          {_formatYearMonth(month.year_month)}
        </Typography>
      </Box>
      <IdentityHeader h={month.identity_header} isDark={isDark} />
      {/* Narrative text is plain, sanitized text (backend strips {}<> at
          write time) — rendered as a text node, never dangerouslySetInnerHTML,
          unlike the news body_html below (which went through a separate,
          dedicated HTML-attribute-stripping sanitizer, _digest_sanitize_body_html). */}
      {month.narrative ? (
        <Typography sx={{ fontSize: "0.92rem", color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
          {month.narrative}
        </Typography>
      ) : (
        <Typography sx={{ color: isDark ? "#64748b" : "#AAA", fontStyle: "italic", fontSize: "0.9rem" }}>
          This month's update isn't available right now.
        </Typography>
      )}
      <MonthNewsSection newsItems={month.news_items} isDark={isDark} />
    </Box>
  );
}

function ReportTypeCard({ rt, isDark }) {
  return (
    <Box sx={{
      mb: 3, borderRadius: 3, overflow: "hidden",
      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.1)",
      bgcolor: isDark ? "#111827" : "#fff",
      boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,53,122,0.06)",
    }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${BRAND.navyDark} 0%, ${BRAND.navy} 100%)`, display: "flex", alignItems: "center", gap: 1.5 }}>
        <MailOutline sx={{ color: BRAND.gold, fontSize: 18 }} />
        {/* report_type_label is a real display name resolved server-side
            (_digest_report_type_display_label) — never the raw
            report_type_key/report_type_id, same bug 11b-2's own close-out
            found and fixed in the backend's own narrative prompt. */}
        {/* ui-ux-checker/advisor finding, pre-handback — native labels are
            lowercase sentence fragments ("toxicology panel", "lab test"),
            unlike Proove's own properly-cased display names; MySummaries.js's
            own equivalent header (line 534) applies the same `capitalize`
            treatment for exactly this reason — matched here. */}
        <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: 14.5, textTransform: "capitalize" }}>{rt.report_type_label}</Typography>
      </Box>
      <Box sx={{ p: 3 }}>
        {rt.months.map((m) => <MonthCard key={m.year_month} month={m} isDark={isDark} />)}
      </Box>
    </Box>
  );
}

function SubscribeGate({ isDark, token }) {
  // 2026-10-16 fix (advisor finding, Part 11c close-out, Open CEO Decision
  // #13 — now closed, not a product question) — this used to link to
  // /my-payment, which only shows the subscription item to a patient who
  // checked the opt-in box at registration (GET /my-payment-items' own
  // display gate, unrelated to this fix, left untouched). A patient who
  // didn't check that box, or registered before the subscription existed,
  // hit a real dead end: a page with nothing to subscribe to. Fixed by
  // calling the SAME mint-checkout-token -> create-checkout-session path
  // MyPayments.js's own handlePay already uses directly — that endpoint
  // has no opt-in gate, so this works for every patient. A patient
  // clicking "Subscribe" right here, right now, IS the opt-in act itself —
  // nothing is offered to anyone who didn't ask for it.
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");

  const handleSubscribe = async () => {
    if (starting) return;
    setStartError("");
    setStarting(true);
    try {
      const mintRes = await axios.post(
        `${API_BASE}/payments/mint-checkout-token`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const res = await axios.post(`${API_BASE}/payments/create-checkout-session`, {
        checkout_token: mintRes.data.checkout_token, item_key: "monthly_ai_subscription",
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setStarting(false);
      setStartError(extractErrorDetail(err, "Could not start checkout — please try again."));
    }
  };

  return (
    <Box sx={{ textAlign: "center", py: 8, color: isDark ? "#94a3b8" : "#64748b" }}>
      <LockOutlined sx={{ fontSize: 40, mb: 1.5, opacity: 0.4 }} />
      <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, mb: 1, color: isDark ? "#e2e8f0" : "#1e293b" }}>
        Subscribe to unlock your monthly digest
      </Typography>
      <Typography sx={{ maxWidth: 420, mx: "auto", mb: 3 }}>
        A monthly AI health insights digest is available with an active subscription — a plain-language
        check-in on your results plus relevant health news, every month.
      </Typography>
      {startError && (
        <Typography sx={{ color: "#dc2626", fontSize: 13.5, mb: 1.5 }}>{startError}</Typography>
      )}
      <Button variant="contained" disabled={starting} onClick={handleSubscribe}
        sx={{ bgcolor: BRAND.navy, "&:hover": { bgcolor: BRAND.navyDark }, borderRadius: 2, px: 3, fontWeight: 700 }}>
        {starting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Subscribe"}
      </Button>
    </Box>
  );
}

export default function MonthlyDigest() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const token = user?.token;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!DIGEST_ENABLED) {
      setLoading(false);
      setError("The monthly digest is not available right now.");
      return;
    }
    axios.get(`${API_BASE}/my-monthly-digest`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.data?.feature_disabled) {
          setError("The monthly digest is not available right now.");
          return;
        }
        setData(res.data);
      })
      .catch(() => setError("Unable to load your digest right now."))
      .finally(() => setLoading(false));
  }, [token]);

  const reportTypes = data?.report_types || [];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f8f6f2" }}>
      <Box sx={{
        px: 3, py: 2, bgcolor: isDark ? "#111827" : "#fff",
        borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)",
        display: "flex", alignItems: "center", gap: 2,
      }}>
        <Box onClick={() => navigate("/home")}
          sx={{
            display: "flex", alignItems: "center", gap: 0.5,
            color: isDark ? "#94a3b8" : "#64748b", fontSize: 14.5, cursor: "pointer",
            borderRadius: "8px", px: 1.2, py: 0.6,
            transition: "all 0.2s",
            "&:hover": { color: isDark ? BRAND.gold : BRAND.navy, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.06)" },
          }}
        >
          <ArrowBack sx={{ fontSize: 15 }} />
          <span>Home</span>
        </Box>
        <Box sx={{ width: "1px", height: 20, bgcolor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,53,122,0.1)" }} />
        <Typography sx={{ fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, fontSize: 18, flex: 1 }}>My Monthly Digest</Typography>
        <ThemeToggle />
        <NavUserMenu />
      </Box>

      <Box sx={{ maxWidth: 760, mx: "auto", px: 3, py: 5 }}>
        <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 4 }}>
          A plain-language monthly check-in on your results, plus relevant health news.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND.navy }} /></Box>
        ) : error ? (
          <Typography sx={{ color: "#ef4444", textAlign: "center", py: 6 }}>{error}</Typography>
        ) : data && data.subscribed === false ? (
          <SubscribeGate isDark={isDark} token={token} />
        ) : reportTypes.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
            <MailOutline sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
            <Typography>Your first monthly digest will appear here once it's ready.</Typography>
          </Box>
        ) : (
          reportTypes.map((rt) => <ReportTypeCard key={rt.report_type_key} rt={rt} isDark={isDark} />)
        )}
      </Box>
    </Box>
  );
}
